<template>
  <div class="content">
    <div class="container-fluid">
      <!-- Header -->
      <div class="row">
        <div class="col-12">
          <div class="card trainer-communication-header">
            <div class="card-header trainer-card-header">
              <h4 class="card-title trainer-title">
                <i class="nc-icon nc-chat-33"></i>
                Comunicación con Clientes
              </h4>
              <p class="card-category trainer-subtitle">
                Chat, notas y comunicación directa con tus atletas
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Interface de Chat -->
      <div class="row">
        <div class="col-lg-4">
          <!-- Lista de Conversaciones -->
          <div class="card trainer-card">
            <div class="card-header">
              <h6 class="card-title">Conversaciones</h6>
              <button class="btn btn-sm trainer-btn-primary" @click="showNewMessageModal = true">
                <i class="nc-icon nc-simple-add"></i>
              </button>
            </div>
            <div class="card-body p-0">
              <div class="trainer-conversations-list">
                <div 
                  v-for="conversation in conversations" 
                  :key="conversation.id"
                  class="trainer-conversation-item"
                  :class="{ 'active': activeConversation === conversation.id }"
                  @click="selectConversation(conversation.id)"
                >
                  <div class="trainer-conversation-avatar">
                    <img :src="conversation.client.avatar" :alt="conversation.client.name">
                    <div v-if="conversation.unread > 0" class="trainer-unread-badge">{{ conversation.unread }}</div>
                  </div>
                  <div class="trainer-conversation-info">
                    <h6>{{ conversation.client.name }}</h6>
                    <p>{{ conversation.lastMessage.text }}</p>
                    <small>{{ formatTime(conversation.lastMessage.time) }}</small>
                  </div>
                  <div class="trainer-conversation-status">
                    <i v-if="conversation.lastMessage.type === 'sent'" class="nc-icon nc-check-2 text-success"></i>
                    <i v-else class="nc-icon nc-minimal-down text-primary"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="col-lg-8">
          <!-- Chat Area -->
          <div class="card trainer-card" v-if="activeConversation">
            <div class="card-header">
              <div class="trainer-chat-header">
                <img :src="selectedConversation.client.avatar" :alt="selectedConversation.client.name" class="trainer-chat-avatar">
                <div>
                  <h6>{{ selectedConversation.client.name }}</h6>
                  <small class="text-muted">Última conexión: hace 2 horas</small>
                </div>
                <div class="trainer-chat-actions ml-auto">
                  <button class="btn btn-sm btn-outline-primary" @click="viewClientProfile">
                    <i class="nc-icon nc-single-02"></i>
                  </button>
                  <button class="btn btn-sm btn-outline-info" @click="scheduleSession">
                    <i class="nc-icon nc-calendar-60"></i>
                  </button>
                </div>
              </div>
            </div>
            <div class="card-body">
              <div class="trainer-chat-messages" ref="chatMessages">
                <div v-for="message in selectedConversation.messages" :key="message.id" 
                     class="trainer-message" :class="{ 'trainer-message-sent': message.sender === 'trainer' }">
                  <div class="trainer-message-content">
                    <p>{{ message.text }}</p>
                    <small>{{ formatTime(message.time) }}</small>
                  </div>
                </div>
              </div>
              
              <div class="trainer-chat-input">
                <div class="input-group">
                  <input 
                    type="text" 
                    class="form-control" 
                    placeholder="Escribe tu mensaje..."
                    v-model="newMessage"
                    @keypress.enter="sendMessage"
                  >
                  <div class="input-group-append">
                    <button class="btn trainer-btn-primary" @click="sendMessage">
                      <i class="nc-icon nc-send"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Placeholder cuando no hay conversación seleccionada -->
          <div class="card trainer-card" v-else>
            <div class="card-body text-center py-5">
              <i class="nc-icon nc-chat-33" style="font-size: 64px; color: #ccc;"></i>
              <h5 class="mt-3">Selecciona una conversación</h5>
              <p class="text-muted">Elige un cliente para comenzar a chatear</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Notas de Clientes -->
      <div class="row">
        <div class="col-12">
          <div class="card trainer-card">
            <div class="card-header">
              <div class="row align-items-center">
                <div class="col-md-6">
                  <h6 class="card-title">Notas y Observaciones</h6>
                </div>
                <div class="col-md-6 text-right">
                  <button class="btn btn-sm trainer-btn-primary" @click="showAddNoteModal = true">
                    <i class="nc-icon nc-simple-add"></i> Nueva Nota
                  </button>
                </div>
              </div>
            </div>
            <div class="card-body">
              <div class="trainer-notes-filter mb-3">
                <div class="row">
                  <div class="col-md-4">
                    <select class="form-control form-control-sm" v-model="notesClientFilter">
                      <option value="">Todos los clientes</option>
                      <option v-for="client in clients" :key="client.id" :value="client.id">
                        {{ client.name }}
                      </option>
                    </select>
                  </div>
                  <div class="col-md-4">
                    <select class="form-control form-control-sm" v-model="notesTypeFilter">
                      <option value="">Todos los tipos</option>
                      <option value="progreso">Progreso</option>
                      <option value="observacion">Observación</option>
                      <option value="objetivo">Objetivo</option>
                      <option value="restriccion">Restricción</option>
                    </select>
                  </div>
                  <div class="col-md-4">
                    <input type="text" class="form-control form-control-sm" placeholder="Buscar en notas..." v-model="notesSearch">
                  </div>
                </div>
              </div>
              
              <div class="trainer-notes-list">
                <div v-for="note in filteredNotes" :key="note.id" class="trainer-note-item">
                  <div class="trainer-note-header">
                    <div class="trainer-note-client">
                      <img :src="note.client.avatar" :alt="note.client.name" class="trainer-note-avatar">
                      <div>
                        <strong>{{ note.client.name }}</strong>
                        <span class="badge trainer-note-type-badge" :class="getNoteTypeClass(note.type)">
                          {{ getNoteTypeLabel(note.type) }}
                        </span>
                      </div>
                    </div>
                    <div class="trainer-note-meta">
                      <small>{{ formatDate(note.date) }}</small>
                      <div class="trainer-note-actions ml-2">
                        <button class="btn btn-sm btn-outline-primary" @click="editNote(note)">
                          <i class="nc-icon nc-settings-gear-65"></i>
                        </button>
                        <button class="btn btn-sm btn-outline-danger" @click="deleteNote(note)">
                          <i class="nc-icon nc-simple-remove"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="trainer-note-content">
                    <p>{{ note.content }}</p>
                    <div v-if="note.tags.length > 0" class="trainer-note-tags">
                      <span v-for="tag in note.tags" :key="tag" class="badge badge-light">{{ tag }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Plantillas de Mensajes -->
      <div class="row">
        <div class="col-lg-6">
          <div class="card trainer-card">
            <div class="card-header">
              <h6 class="card-title">Plantillas de Mensajes</h6>
            </div>
            <div class="card-body">
              <div class="trainer-templates-list">
                <div v-for="template in messageTemplates" :key="template.id" class="trainer-template-item" @click="useTemplate(template)">
                  <div class="trainer-template-icon">
                    <i :class="template.icon"></i>
                  </div>
                  <div class="trainer-template-content">
                    <h6>{{ template.name }}</h6>
                    <p>{{ template.content }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="col-lg-6">
          <div class="card trainer-card">
            <div class="card-header">
              <h6 class="card-title">Recordatorios Automáticos</h6>
            </div>
            <div class="card-body">
              <div class="trainer-reminders-list">
                <div class="trainer-reminder-item">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" id="reminder1" checked>
                    <label class="form-check-label" for="reminder1">
                      <strong>Recordatorio de sesión</strong>
                      <br><small>2 horas antes de cada sesión</small>
                    </label>
                  </div>
                </div>
                
                <div class="trainer-reminder-item">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" id="reminder2" checked>
                    <label class="form-check-label" for="reminder2">
                      <strong>Seguimiento semanal</strong>
                      <br><small>Cada lunes por la mañana</small>
                    </label>
                  </div>
                </div>
                
                <div class="trainer-reminder-item">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" id="reminder3">
                    <label class="form-check-label" for="reminder3">
                      <strong>Evaluación mensual</strong>
                      <br><small>1 semana antes del vencimiento</small>
                    </label>
                  </div>
                </div>
              </div>
              
              <button class="btn btn-outline-primary btn-sm w-100 mt-3">
                <i class="nc-icon nc-settings-gear-65"></i> Configurar Recordatorios
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Nuevo Mensaje -->
    <div class="modal" v-if="showNewMessageModal" @click="showNewMessageModal = false">
      <div class="modal-dialog" @click.stop>
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Nuevo Mensaje</h5>
            <button type="button" class="close" @click="showNewMessageModal = false">
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <form>
              <div class="form-group">
                <label>Cliente</label>
                <select class="form-control">
                  <option value="">Seleccionar cliente</option>
                  <option v-for="client in clients" :key="client.id" :value="client.id">
                    {{ client.name }}
                  </option>
                </select>
              </div>
              <div class="form-group">
                <label>Mensaje</label>
                <textarea class="form-control" rows="4" placeholder="Escribe tu mensaje..."></textarea>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showNewMessageModal = false">Cancelar</button>
            <button type="button" class="btn trainer-btn-primary" @click="createNewConversation">Enviar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Nueva Nota -->
    <div class="modal" v-if="showAddNoteModal" @click="showAddNoteModal = false">
      <div class="modal-dialog" @click.stop>
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Nueva Nota</h5>
            <button type="button" class="close" @click="showAddNoteModal = false">
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <form>
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Cliente</label>
                    <select class="form-control">
                      <option value="">Seleccionar cliente</option>
                      <option v-for="client in clients" :key="client.id" :value="client.id">
                        {{ client.name }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Tipo de Nota</label>
                    <select class="form-control">
                      <option value="progreso">Progreso</option>
                      <option value="observacion">Observación</option>
                      <option value="objetivo">Objetivo</option>
                      <option value="restriccion">Restricción</option>
                    </select>
                  </div>
                </div>
              </div>
              <div class="form-group">
                <label>Contenido</label>
                <textarea class="form-control" rows="4" placeholder="Escribe tu nota..."></textarea>
              </div>
              <div class="form-group">
                <label>Etiquetas (separadas por coma)</label>
                <input type="text" class="form-control" placeholder="fuerza, cardio, nutrición...">
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showAddNoteModal = false">Cancelar</button>
            <button type="button" class="btn trainer-btn-primary" @click="addNote">Guardar Nota</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Communication',
  data() {
    return {
      activeConversation: 1,
      newMessage: '',
      showNewMessageModal: false,
      showAddNoteModal: false,
      notesClientFilter: '',
      notesTypeFilter: '',
      notesSearch: '',
      clients: [
        { id: 1, name: 'Ana Martínez' },
        { id: 2, name: 'Carlos Ruiz' },
        { id: 3, name: 'María González' },
        { id: 4, name: 'José López' }
      ],
      conversations: [
        {
          id: 1,
          client: {
            name: 'Ana Martínez',
            avatar: '/static/img/faces/ayo-ogunseinde-2.jpg'
          },
          unread: 2,
          lastMessage: {
            text: '¡Gracias por la rutina! ¿Cuándo es mi próxima evaluación?',
            time: '2024-01-19T10:30:00',
            type: 'received'
          },
          messages: [
            {
              id: 1,
              sender: 'client',
              text: 'Hola, tengo una pregunta sobre la rutina de hoy',
              time: '2024-01-19T09:15:00'
            },
            {
              id: 2,
              sender: 'trainer',
              text: 'Hola Ana, ¡claro! Dime, ¿qué necesitas saber?',
              time: '2024-01-19T09:18:00'
            },
            {
              id: 3,
              sender: 'client',
              text: '¿Puedo hacer cardio después de las sentadillas?',
              time: '2024-01-19T09:20:00'
            },
            {
              id: 4,
              sender: 'trainer',
              text: 'Perfecto, sí puedes. Te recomiendo 15-20 minutos de cardio moderado para completar la sesión.',
              time: '2024-01-19T09:25:00'
            },
            {
              id: 5,
              sender: 'client',
              text: '¡Gracias por la rutina! ¿Cuándo es mi próxima evaluación?',
              time: '2024-01-19T10:30:00'
            }
          ]
        },
        {
          id: 2,
          client: {
            name: 'Carlos Ruiz',
            avatar: '/static/img/faces/erik-lucatero-2.jpg'
          },
          unread: 0,
          lastMessage: {
            text: 'Perfecto, nos vemos mañana entonces',
            time: '2024-01-18T16:45:00',
            type: 'sent'
          },
          messages: [
            {
              id: 1,
              sender: 'trainer',
              text: 'Hola Carlos, ¿cómo va todo? ¿Pudiste completar la rutina de ayer?',
              time: '2024-01-18T16:30:00'
            },
            {
              id: 2,
              sender: 'client',
              text: 'Hola! Sí, la completé toda. Estuvo intensa pero me sentí muy bien',
              time: '2024-01-18T16:35:00'
            },
            {
              id: 3,
              sender: 'trainer',
              text: 'Excelente. Mañana aumentaremos un poco el peso en press de banca',
              time: '2024-01-18T16:40:00'
            },
            {
              id: 4,
              sender: 'client',
              text: 'Perfecto, nos vemos mañana entonces',
              time: '2024-01-18T16:45:00'
            }
          ]
        }
      ],
      notes: [
        {
          id: 1,
          client: {
            name: 'Ana Martínez',
            avatar: '/static/img/faces/ayo-ogunseinde-2.jpg'
          },
          type: 'progreso',
          content: 'Excelente progreso en sentadillas. Ha aumentado 10kg en las últimas 3 semanas. Mostrar más ejercicios de glúteo para complementar.',
          date: '2024-01-18',
          tags: ['fuerza', 'piernas', 'progreso']
        },
        {
          id: 2,
          client: {
            name: 'Carlos Ruiz',
            avatar: '/static/img/faces/erik-lucatero-2.jpg'
          },
          type: 'restriccion',
          content: 'Molestia leve en hombro derecho. Evitar press militar por 1 semana. Enfocarse en ejercicios de rotadores.',
          date: '2024-01-17',
          tags: ['lesión', 'hombro', 'precaución']
        },
        {
          id: 3,
          client: {
            name: 'María González',
            avatar: '/static/img/faces/avatar.png'
          },
          type: 'objetivo',
          content: 'Cliente quiere enfocarse más en cardio para su evento en 2 meses. Ajustar plan para incluir más HIIT.',
          date: '2024-01-16',
          tags: ['cardio', 'objetivo', 'evento']
        }
      ],
      messageTemplates: [
        {
          id: 1,
          name: 'Recordatorio de Sesión',
          icon: 'nc-icon nc-bell-55',
          content: 'Hola! Te recuerdo que tienes sesión mañana a las [HORA]. ¡Te espero!'
        },
        {
          id: 2,
          name: 'Felicitaciones por Progreso',
          icon: 'nc-icon nc-trophy',
          content: '¡Excelente trabajo en la sesión de hoy! Tu dedicación se nota. ¡Sigue así!'
        },
        {
          id: 3,
          name: 'Ajuste de Rutina',
          icon: 'nc-icon nc-settings-gear-65',
          content: 'He actualizado tu rutina basándome en tu progreso. Revisa los cambios en la app.'
        },
        {
          id: 4,
          name: 'Motivación Semanal',
          icon: 'nc-icon nc-chat-33',
          content: '¡Esta semana vas excelente! Recuerda que cada día te acerca más a tu objetivo.'
        }
      ]
    }
  },
  computed: {
    selectedConversation() {
      return this.conversations.find(conv => conv.id === this.activeConversation);
    },
    filteredNotes() {
      let filtered = this.notes;
      
      if (this.notesClientFilter) {
        filtered = filtered.filter(note => note.client.id == this.notesClientFilter);
      }
      
      if (this.notesTypeFilter) {
        filtered = filtered.filter(note => note.type === this.notesTypeFilter);
      }
      
      if (this.notesSearch) {
        filtered = filtered.filter(note => 
          note.content.toLowerCase().includes(this.notesSearch.toLowerCase()) ||
          note.client.name.toLowerCase().includes(this.notesSearch.toLowerCase())
        );
      }
      
      return filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
    }
  },
  methods: {
    selectConversation(id) {
      this.activeConversation = id;
      // Marcar como leído
      const conversation = this.conversations.find(conv => conv.id === id);
      if (conversation) {
        conversation.unread = 0;
      }
    },
    sendMessage() {
      if (this.newMessage.trim() && this.selectedConversation) {
        const newMsg = {
          id: Date.now(),
          sender: 'trainer',
          text: this.newMessage,
          time: new Date().toISOString()
        };
        
        this.selectedConversation.messages.push(newMsg);
        this.selectedConversation.lastMessage = {
          text: this.newMessage,
          time: new Date().toISOString(),
          type: 'sent'
        };
        
        this.newMessage = '';
        
        // Scroll al final
        this.$nextTick(() => {
          if (this.$refs.chatMessages) {
            this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight;
          }
        });
      }
    },
    formatTime(timeString) {
      const date = new Date(timeString);
      return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES');
    },
    getNoteTypeClass(type) {
      const classes = {
        'progreso': 'badge-success',
        'observacion': 'badge-info',
        'objetivo': 'badge-warning',
        'restriccion': 'badge-danger'
      };
      return classes[type] || 'badge-secondary';
    },
    getNoteTypeLabel(type) {
      const labels = {
        'progreso': 'Progreso',
        'observacion': 'Observación',
        'objetivo': 'Objetivo',
        'restriccion': 'Restricción'
      };
      return labels[type] || type;
    },
    useTemplate(template) {
      this.newMessage = template.content;
    },
    viewClientProfile() {
      this.$swal.fire({
        title: 'Perfil del Cliente',
        text: `Ver perfil de ${this.selectedConversation.client.name}`,
        confirmButtonColor: '#9ACD32'
      });
    },
    scheduleSession() {
      this.$swal.fire({
        title: 'Programar Sesión',
        text: `Programar sesión con ${this.selectedConversation.client.name}`,
        confirmButtonColor: '#9ACD32'
      });
    },
    createNewConversation() {
      this.showNewMessageModal = false;
      this.$swal.fire({
        title: 'Mensaje Enviado',
        text: 'El mensaje ha sido enviado exitosamente',
        icon: 'success',
        confirmButtonColor: '#9ACD32'
      });
    },
    addNote() {
      this.showAddNoteModal = false;
      this.$swal.fire({
        title: 'Nota Guardada',
        text: 'La nota ha sido añadida exitosamente',
        icon: 'success',
        confirmButtonColor: '#9ACD32'
      });
    },
    editNote(note) {
      this.$swal.fire({
        title: 'Editar Nota',
        text: `Editar nota del ${this.formatDate(note.date)}`,
        confirmButtonColor: '#9ACD32'
      });
    },
    deleteNote(note) {
      this.$swal.fire({
        title: '¿Eliminar nota?',
        text: 'Esta acción no se puede deshacer',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#9ACD32',
        confirmButtonText: 'Eliminar',
        cancelButtonText: 'Cancelar'
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.content {
  --trainer-primary: #9ACD32;
  --trainer-secondary: #7CB342;
  --trainer-accent: #8BC34A;
  --trainer-dark: #689F38;
  --trainer-light: #DCEDC8;
  --trainer-bg: #F1F8E9;
}

.trainer-communication-header {
  background: linear-gradient(135deg, var(--trainer-primary), var(--trainer-secondary));
  color: white;
  border: none;
  box-shadow: 0 4px 20px 0px rgba(154, 205, 50, 0.14), 0 7px 10px -5px rgba(154, 205, 50, 0.4);
  
  .trainer-card-header {
    background: transparent;
    border: none;
  }
  
  .trainer-title {
    color: white;
    font-weight: 500;
    margin-bottom: 5px;
    
    i {
      margin-right: 10px;
    }
  }
  
  .trainer-subtitle {
    color: rgba(255,255,255,0.8);
    margin: 0;
  }
}

.trainer-card {
  border: none;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
  margin-bottom: 20px;
  
  .card-header {
    background: var(--trainer-bg);
    border-bottom: 1px solid #dee2e6;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .card-title {
      color: var(--trainer-dark);
      font-weight: 600;
      margin: 0;
    }
  }
}

.trainer-conversations-list {
  max-height: 600px;
  overflow-y: auto;
  
  .trainer-conversation-item {
    display: flex;
    align-items: center;
    padding: 15px;
    border-bottom: 1px solid #e9ecef;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover, &.active {
      background: var(--trainer-bg);
    }
    
    .trainer-conversation-avatar {
      position: relative;
      margin-right: 12px;
      
      img {
        width: 45px;
        height: 45px;
        border-radius: 50%;
        object-fit: cover;
      }
      
      .trainer-unread-badge {
        position: absolute;
        top: -5px;
        right: -5px;
        background: var(--trainer-primary);
        color: white;
        border-radius: 50%;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        font-weight: 600;
      }
    }
    
    .trainer-conversation-info {
      flex: 1;
      
      h6 {
        margin: 0 0 5px 0;
        color: var(--trainer-dark);
        font-weight: 600;
      }
      
      p {
        margin: 0 0 5px 0;
        color: #666;
        font-size: 14px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 180px;
      }
      
      small {
        color: #999;
        font-size: 12px;
      }
    }
    
    .trainer-conversation-status {
      margin-left: 10px;
      
      i {
        font-size: 16px;
      }
    }
  }
}

.trainer-chat-header {
  display: flex;
  align-items: center;
  
  .trainer-chat-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    margin-right: 12px;
    object-fit: cover;
  }
  
  h6 {
    margin: 0;
    color: var(--trainer-dark);
  }
  
  .trainer-chat-actions {
    display: flex;
    gap: 8px;
  }
}

.trainer-chat-messages {
  height: 400px;
  overflow-y: auto;
  padding: 15px 0;
  
  .trainer-message {
    display: flex;
    margin-bottom: 15px;
    
    &.trainer-message-sent {
      justify-content: flex-end;
      
      .trainer-message-content {
        background: var(--trainer-primary);
        color: white;
      }
    }
    
    .trainer-message-content {
      max-width: 70%;
      background: #f8f9fa;
      padding: 12px 15px;
      border-radius: 18px;
      
      p {
        margin: 0 0 5px 0;
        line-height: 1.4;
      }
      
      small {
        opacity: 0.7;
        font-size: 11px;
      }
    }
  }
}

.trainer-chat-input {
  border-top: 1px solid #e9ecef;
  padding-top: 15px;
  
  .input-group {
    .form-control {
      border: 2px solid #e9ecef;
      border-radius: 25px;
      
      &:focus {
        border-color: var(--trainer-primary);
        box-shadow: none;
      }
    }
    
    .input-group-append .btn {
      border-radius: 50%;
      width: 40px;
      height: 40px;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
}

.trainer-notes-list {
  .trainer-note-item {
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 15px;
    
    .trainer-note-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 15px;
      
      .trainer-note-client {
        display: flex;
        align-items: center;
        
        .trainer-note-avatar {
          width: 35px;
          height: 35px;
          border-radius: 50%;
          margin-right: 10px;
          object-fit: cover;
        }
        
        .trainer-note-type-badge {
          margin-left: 10px;
          font-size: 11px;
          padding: 4px 8px;
        }
      }
      
      .trainer-note-meta {
        display: flex;
        align-items: center;
        
        .trainer-note-actions {
          display: flex;
          gap: 5px;
          
          .btn {
            padding: 4px 8px;
            font-size: 12px;
          }
        }
      }
    }
    
    .trainer-note-content {
      p {
        color: #333;
        line-height: 1.6;
        margin-bottom: 10px;
      }
      
      .trainer-note-tags {
        display: flex;
        gap: 5px;
        flex-wrap: wrap;
        
        .badge {
          font-size: 11px;
          padding: 4px 8px;
        }
      }
    }
  }
}

.trainer-templates-list {
  .trainer-template-item {
    display: flex;
    align-items: center;
    padding: 15px;
    background: #f8f9fa;
    border-radius: 6px;
    margin-bottom: 15px;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      background: var(--trainer-light);
    }
    
    .trainer-template-icon {
      width: 40px;
      height: 40px;
      background: var(--trainer-primary);
      color: white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 15px;
      
      i {
        font-size: 18px;
      }
    }
    
    .trainer-template-content {
      flex: 1;
      
      h6 {
        color: var(--trainer-dark);
        margin-bottom: 5px;
        font-weight: 600;
      }
      
      p {
        margin: 0;
        color: #666;
        font-size: 14px;
        line-height: 1.4;
      }
    }
  }
}

.trainer-reminders-list {
  .trainer-reminder-item {
    padding: 15px;
    background: #f8f9fa;
    border-radius: 6px;
    margin-bottom: 15px;
    
    .form-check-label {
      cursor: pointer;
      
      strong {
        color: var(--trainer-dark);
      }
      
      small {
        color: #666;
      }
    }
  }
}

.trainer-btn-primary {
  background: var(--trainer-primary);
  border-color: var(--trainer-primary);
  color: white;
  
  &:hover {
    background: var(--trainer-secondary);
    border-color: var(--trainer-secondary);
  }
}

.form-control {
  border: 2px solid #e9ecef;
  border-radius: 6px;
  
  &:focus {
    border-color: var(--trainer-primary);
    box-shadow: 0 0 0 0.2rem rgba(154, 205, 50, 0.25);
  }
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  
  .modal-dialog {
    background: white;
    border-radius: 8px;
    max-width: 90%;
    max-height: 90%;
    overflow-y: auto;
  }
  
  .modal-header {
    padding: 20px;
    border-bottom: 1px solid #dee2e6;
    
    .modal-title {
      color: var(--trainer-dark);
      font-weight: 600;
    }
    
    .close {
      background: none;
      border: none;
      font-size: 24px;
      line-height: 1;
      color: #999;
      cursor: pointer;
    }
  }
  
  .modal-body {
    padding: 20px;
  }
  
  .modal-footer {
    padding: 20px;
    border-top: 1px solid #dee2e6;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }
}
</style>